<template>
  <div class="well">
    <ais-sort-by
      v-show="false"
      :items="sortByItems"/>
    <ais-panel>
      <h2 class="filter-header">Subjects</h2>
      <ais-menu-select
        attribute="subject"
        :limit="25"
        :transform-items="transformSubjectItems">
        <template v-slot="{ items, refine }">
          <A11yDropdown
            :dropdown-props="{
              ref:'subject-dropdown',
              class: 'browse-assessments-subject-dropdown',
            }"
            :button-props="{
              class: 'btn btn-default dropdown-toggle ellipsis',
              'data-role': 'button',
              'aria-label': selectedSubject ? `Subject: ${selectedSubject}` : 'Select a subject'
            }"
            :dom-attributes="{
              role: 'listbox',
              'aria-label': selectedSubject ? `Subject: ${selectedSubject}` : 'Select a subject'
            }"
          >
            <template slot="button-contents">
              {{ subjectFilterText }} <i class="fa fa-caret-down" />
            </template>
            <template slot="dropdown-items">
              <li
                v-for="subject in items"
                :key="subject.value"
                role="option"
                @click="onSubjectChanged(subject.value); refine(subject.value)">
                <a class="subject-option">{{ subject.label }}</a>
              </li>
            </template>
          </A11yDropdown>
        </template>
      </ais-menu-select>
    </ais-panel>
    <ais-panel>
      <h2 class="filter-header">Grades</h2>
      <ais-refinement-list
        v-if="isSubjectSelected && showGrades"
        attribute="grades"
        :limit="20"
        :class-names="{
          'ais-RefinementList': 'ais-RefinementListGrades'
        }"
        :transform-items="transformGrades"
      >
        <template v-slot:item="{ item, refine }">
          <label
            class="ais-RefinementList-label grades"
            :class="{'selected': item.isRefined, 'disabled': item.count < 1}">
            <LockableInputWrapper :locked="!isPremiumUser">
              <input
                class="ais-RefinementList-checkbox"
                type="checkbox"
                tabindex="0"
                :value="item.value"
                :checked="item.isRefined"
                :disabled="!isPremiumUser"
                @change="onGradeChanged(item, refine, $event)"
              >
            </LockableInputWrapper>
            <span class="ais-RefinementList-labelText">{{ item.label }}</span>
          </label>
        </template>
      </ais-refinement-list>
      <div v-if="!isSubjectSelected" class="disabled">Select a subject</div>
      <div v-if="!showGrades && isSubjectSelected" class="disabled">N/A</div>
    </ais-panel>
    <ais-panel>
      <h2 class="filter-header">Categories</h2>
      <ais-menu
        v-if="isSubjectSelected && !loading && categoryOptionsForSelectedSubject.length"
        :attribute=categoryAttribute
        operator="or"
        :limit="25"
        :transform-items="transformCategoryItems">
        <template v-slot="{ items, refine }">
          <ul>
            <li v-for="item in items" :key="item.value">
              <label class="ais-RefinementList-label" :class="{'selected': item.isRefined}">
                <LockableInputWrapper
                  :locked="!isPremiumUser">
                  <input
                    class="ais-RefinementList-radio"
                    type="radio"
                    role="radio"
                    tabindex="0"
                    :value="item.value"
                    :checked="item.isRefined"
                    :disabled="!isPremiumUser"
                    v-if="item.count >= 1"
                    @click="sendFilterEvent('domains', item); refine(item.value)"
                  >
                </LockableInputWrapper>
                <span class="ais-RefinementList-labelText" :class="{'disabled': item.count < 1}">{{ item.label }}</span>
              </label>
            </li>
          </ul>
        </template>
      </ais-menu>
      <div v-else-if="!isSubjectSelected" class="disabled">Select a subject</div>
      <p v-else-if="!categoryOptionsForSelectedSubject.length" class="text-muted">
        <i>No applicable categories</i>
      </p>
    </ais-panel>
    <ais-panel>
      <h2 class="filter-header">Assessment Type</h2>
      <ais-menu
        v-if="isSubjectSelected && !loading"
        attribute="type"
        operator="or"
        :limit="25"
        :transform-items="transformTypeItems">
        <template v-slot="{ items, refine }">
          <ul>
            <li v-for="item in items" :key="item.value">
              <label class="ais-RefinementList-label" :class="{'selected': item.isRefined}">
                <LockableInputWrapper :locked="false">
                  <input
                    class="ais-RefinementList-radio"
                    type="radio"
                    role="radio"
                    tabindex="0"
                    :value="item.value"
                    :checked="item.isRefined"
                    v-if="item.count >= 1"
                    @click="sendFilterEvent('type', item); refine(item.value)">
                </LockableInputWrapper>
                <span class="ais-RefinementList-labelText" :class="{'disabled': item.count < 1}">{{ item.label }}</span>
              </label>
            </li>
          </ul>
        </template>
      </ais-menu>
      <div v-else-if="!isSubjectSelected" class="disabled">Select a subject</div>
    </ais-panel>
    <ais-panel>
      <h2 class="filter-header">Search</h2>
      <ais-search-box :search-client="searchClient" :index-name="algoliaIndex">
        <debounced-search-box
          @updatedQuery="onUpdatedQuery"
          :disabled="!isPremiumUser"
          :ref-name="searchBoxRefName"/>
      </ais-search-box>
    </ais-panel>
  </div>
</template>
<script>
import { AisDynamicWidgets, AisRefinementList, AisPanel, AisMenuSelect, AisStateResults, AisSortBy, AisMenu } from 'vue-instantsearch';
import A11yDropdown from '@/components/A11yDropdown'
import DebouncedSearchBox from '@/components/search/DebouncedSearchBox.vue'
import { getEnrolledGradesSet } from '@/api/presentlevels'
import LockableInputWrapper from '@/components/LockableInputWrapper.vue'
import { mapGetters } from 'vuex';
import { noSelMixin } from '@/contentprofile/no_sel_mixins'

export default {
  name: 'BrowseAssessmentsFilters',
  components: {
    DebouncedSearchBox,
    AisDynamicWidgets,
    AisMenuSelect,
    AisPanel,
    AisRefinementList,
    AisStateResults,
    AisSortBy,
    A11yDropdown,
    AisMenu,
    LockableInputWrapper
  },
  mixins: [ noSelMixin ],
  props: {
    algoliaIndex: {
      type: String,
      required: true
    },
    showGrades: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    },
    searchClient: {
      type: Object,
      required: true
    },
    onUpdatedQuery: {
      type: Function,
      required: true
    },
    selectedSubject: {
      type: String,
      default: null
    },
    keywordQuery: {
      type: String,
      default: ''
    },
    categoryOptions: {
      type: Object,
      required: false,
      default: () => {}
    },
    defaultSubjects: {
      type: Array,
      default: () => []
    },
    searchBoxRefName: {
      type: String,
      required: true
    }
  },
  methods: {
    sendFilterEvent(filterType, filterItem) {
      let eventAttrs = {}
      if (filterType === 'domains' && filterItem.isRefined) {
        eventAttrs['browse_assessments_unselected_category'] = filterItem.value
        this.$sendEvent('Unselected Browse Assessments Category', eventAttrs)

      } else if (filterType === 'domains' && !filterItem.isRefined) {
        eventAttrs['browse_assessments_selected_category'] = filterItem.value
        this.$sendEvent('Selected Browse Assessments Category', eventAttrs)

      } else if (filterType === 'grades' && filterItem.isRefined) {
        eventAttrs['browse_assessments_unselected_grade'] = filterItem.value
        this.$sendEvent('Unselected Browse Assessments Grade', eventAttrs)

      } else if (filterType === 'type' && filterItem.isRefined) {
        eventAttrs['browse_assessments_unselected_type'] = filterItem.value
        this.$sendEvent('Unselected Browse Assessments Type', eventAttrs)

      } else if (filterType === 'type' && !filterItem.isRefined) {
        eventAttrs['browse_assessments_selected_type'] = filterItem.value
        this.$sendEvent('Selected Browse Assessments Type', eventAttrs)

      } else if (filterType === 'grades' && !filterItem.isRefined) {
        eventAttrs['browse_assessments_selected_grade'] = filterItem.value
        this.$sendEvent('Selected Browse Assessments Grade', eventAttrs)
      }
    },
    clearFocus(event, isRefined) {
      if (isRefined) {
        event.target.blur();
      }
    },
    onGradeChanged(item, refine, event) {
      this.sendFilterEvent('grades', item)
      this.clearFocus(event, item.isRefined)
      refine(item.value)
    },
    onSubjectChanged(subject) {
      this.$sendEvent('Selected Browse Assessments Subject', {
        browse_assessments_selected_subject: subject
      })
      this.$emit('subject-changed')
    },
    transformSubjectItems(items, { results }) {
      let newItems = []
      for (let subjectOption of this.assessmentSubjectOptionsForProfile) {
        newItems.push({
          label: subjectOption.getLabel(),
          count: 0,
          isRefined: items.find((item) => item.value === subjectOption.getValue())?.isRefined,
          value: subjectOption.getValue(),
          highlighted: subjectOption.getValue()
        })
      }
      return newItems
    },
    transformCategoryItems(items, { results }) {
      let newItems = []
      for (let categoryOption of this.categoryOptionsForSelectedSubject) {
        const foundItem = items.find(item => item.value === categoryOption.getValue())
        if (foundItem) {
          newItems.push(foundItem)
        } else {
          newItems.push({
            count: 0,
            highlighted: categoryOption.getValue(),
            isRefined: items.find((item) => item.value === categoryOption.getValue())?.isRefined,
            label: categoryOption.getLabel(),
            value: categoryOption.getValue()
          })
        }
      }
      return newItems
    },
    transformGrades(items, {results}) {
      let itemLabels = items.map(item => item.label)
      let gradesNotFound = this.defaultGrades.filter(grade => !itemLabels.includes(grade))
      let existingItems = items.map((item) => {
        return {
          ...item,
          label: this.cleanGradeLabel(item.label),
        }
      });
      if (gradesNotFound.length > 0) {
        let newItems = []
        this.defaultGrades.forEach((grade) => {
          if (gradesNotFound.includes(grade)) {
            newItems.push({
              count: 0,
              highlighted: grade,
              isRefined: false,
              label: this.cleanGradeLabel(grade),
              value: grade
            })
          } else {
            newItems.push(existingItems.find(item => item.value === grade))
          }
        })
        return newItems
      }
      return existingItems
    },
    transformTypeItems(items, {results}) {
      let newItems = []
      for (let typeItem of ['Baseline', 'Progress Monitoring']) {
        const foundItem = items.find(item => item.value === typeItem)
        if (foundItem) {
          newItems.push(foundItem)
        } else {
          newItems.push({
            count: 0,
            highlighted: typeItem,
            isRefined: items.find((item) => item.value === typeItem)?.isRefined,
            label: typeItem,
            value: typeItem
          })
        }
      }
      return newItems
    },
    cleanGradeLabel(grade) {
      let label;
      if (grade === 'Pre-K' || grade === 'K') {
        label = grade
      } else if (grade === 'Adult-Transition') {
        label = 'Adult Transition'
      } else {
        label = parseInt(grade)
      }
      return label
    }
  },
  computed: {
    ...mapGetters({
      isPremiumUser: 'user/isPremium',
    }),
    subjectFilterText() { return this.selectedSubject || 'Select a subject' },
    categoryAttribute() { return this.isBehaviorSelected == true ? 'behavior_category': 'domains' },
    defaultGrades() {
      return getEnrolledGradesSet().map((grade) => {
        return grade.val.replace(/(st|nd|rd|th)$/i, '')
      })
    },
    isSubjectSelected() { return !!this.selectedSubject },
    isBehaviorSelected() { return this.selectedSubject === 'Behavior'},
    categoryOptionsForSelectedSubject() {
      return this.assessmentSubjectCategoriesForProfile(this.selectedSubject)
    },
    sortByItems() {
      if (this.keywordQuery) {
        return [{value: this.algoliaIndex, label: 'relevancy'}]
      } else {
        return [{value: this.algoliaIndex+'_sort_key_asc', label: 'sort_key'}]
      }
    }
  }
}

</script>
<style lang="less" scoped>
@import '~@/variables.less';
.well {
  padding: 0;
}
.ais-Panel {
  margin: 1rem;
}
::v-deep {
  .disabled {
    cursor: not-allowed;
    color: @gray-light;
    font-style: italic;
    font-weight: normal;
  }
  .ais-RefinementList-labelText {
    padding: 0px 5px;
    margin: 0.1rem;
  }
  .ais-RefinementList-item {
    display: block;
    cursor: pointer;
    .ais-RefinementList-label {
      border-radius: 10px;
      display: block;
    }
    &:hover .ais-RefinementList-label:not(.selected):not(.disabled) {
      background: @gray-lighter;
    }
  }
  .ais-RefinementListGrades {
    .ais-RefinementList-list {
      display: flex;
      flex-flow: wrap;
    }
    .ais-RefinementList-item {
      display: flex;
      flex-basis: 3.2em;
      .disabled {
        input {
          visibility: hidden;
        }
        .ais-RefinementList-labelText {
          cursor: not-allowed;
          color: @gray-light;
        }
      }
    }
    .ais-RefinementList-label {
      flex-direction: column;
      flex-wrap: wrap;
      flex-grow: 1;
      &.grades{
        display: flex;
        align-items: center;
        justify-content: flex-start;
        width: 2.75rem;

        .ais-RefinementList-labelText {
          line-height: 1.5;
          margin-bottom: 0.25rem;
        }
      }
    }
  }
  .ais-RefinementList-checkbox {
    box-shadow: none;
    border-color: var(--gray-weak);
  }
  .ais-RefinementList-labelText {
    cursor: pointer;
    font-weight: normal;
    color: #245682;
  }
  .ais-RefinementList-labelText {
    font-weight: normal;
    color: #245682;
    margin-bottom: -2px;
  }
  .selected:not(.disabled) {
    background-color: @gb_blue_medium;
    .ais-RefinementList-labelText {
      color: #FFF;
    }
    .ais-RefinementList-checkbox {
      appearance: auto;
    }
  }
  .ais-Menu ul {
    list-style: none;
    padding-left: 0;
    .ais-RefinementList-label {
      padding: 5px;
      border-radius: 10px;
      &:hover:not(.selected) {
        background-color: @gray-lighter
      }
    }
    .ais-RefinementList-labelText.disabled {
      margin-left: .8rem;
      cursor: not-allowed;
      color: @gray-light;
      font-style: italic;
      font-weight: normal
    }
  }
  .ais-SearchBox {
    padding-bottom: 20px;
  }
  .ais-RefinementList-count {
    display: none;
  }

  .browse-assessments-subject-dropdown {
    width: 100%;
    .dropdown-menu li {
      cursor: pointer;
      a:hover {
        background-color: var(--blue-default);
        color: white;
      }
    }
    a.dropdown-toggle {
      width: 100%;
      text-align: left;
      display: flex;
      align-items: center;
      border-color: @gray-light;
      &:hover {
        border-color: var(--gray-strong);
      }
    }
    .fa {
      margin-left: auto
    }
  }
  .lockable-input-wrapper .lockable-input .input {
    display: flex;
  }
}
</style>
